import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  TableHead,
} from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { StatusBadge as AppStatusBadge } from '@/components/ui/status-badge';
import { FileText, Download, Bell, Search, Loader2 } from 'lucide-react';
import { Membre, getAttestationStatus } from '@/hooks/queries/useMembresQuery';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface EquipeTableProps {
  membres: Membre[];
  loading: boolean;
  fetched: boolean;
  onSelectMembre: (membre: Membre) => void;
}

type ContratFilter = 'tous' | 'CDI' | 'CDD' | 'Benevole' | 'Stage' | 'Interim';
type StatutFilter = 'tous' | 'conforme' | 'en_attente' | 'a_regulariser';

/**
 * Badge de statut d'attestation avec les couleurs existantes du projet
 */
function AttestationBadge({ status }: { status: ReturnType<typeof getAttestationStatus> }) {
  switch (status) {
    case 'conforme':
      return (
        <AppStatusBadge tone="success">
          Conforme
        </AppStatusBadge>
      );
    case 'en_attente':
      return (
        <AppStatusBadge tone="warning">
          En attente
        </AppStatusBadge>
      );
    case 'verification_en_cours':
      return (
        <AppStatusBadge tone="info">
          <Loader2 className="w-3 h-3 animate-spin" />
          Vérification en cours
        </AppStatusBadge>
      );
    case 'expiree':
      return (
        <AppStatusBadge tone="danger">
          Expirée
        </AppStatusBadge>
      );
    case 'a_regulariser':
      return (
        <AppStatusBadge tone="danger">
          À régulariser
        </AppStatusBadge>
      );
  }
}

/**
 * Badge de type de contrat
 */
function ContratBadge({ type }: { type: string }) {
  const label = type === 'Benevole' ? 'Bénévole' : type === 'Interim' ? 'Intérim' : type;

  return (
    <AppStatusBadge tone="neutral">
      {label}
    </AppStatusBadge>
  );
}

/**
 * Formate une date en français court
 */
function formatDateShort(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'd MMM yyyy', { locale: fr });
  } catch {
    return '—';
  }
}

/**
 * Affiche la date de renouvellement avec warning visuel si < 30 jours
 */
function RenouvellementCell({ date }: { date: string | null }) {
  if (!date) return <span className="text-neutral-400">—</span>;
  
  const now = new Date();
  const renouvellement = new Date(date);
  const daysUntil = Math.ceil((renouvellement.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysUntil < 0) {
    return (
      <span className="flex items-center gap-1 text-sm font-medium text-rose-600">
        ⚠ Dépassé
      </span>
    );
  }
  
  if (daysUntil <= 30) {
    return (
      <span className="flex items-center gap-1 text-sm font-medium text-amber-600">
        ⚠ {formatDateShort(date)}
      </span>
    );
  }
  
  return <span className="text-sm text-[hsl(var(--app-text))]">{formatDateShort(date)} ✓</span>;
}

export const EquipeTable = ({ membres, loading, fetched, onSelectMembre }: EquipeTableProps) => {
  const { toast } = useToast();
  const [contratFilter, setContratFilter] = useState<ContratFilter>('tous');
  const [statutFilter, setStatutFilter] = useState<StatutFilter>('tous');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtrer les membres
  const filteredMembres = useMemo(() => {
    return membres.filter(membre => {
      // Filtre recherche
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const fullName = `${membre.prenom} ${membre.nom}`.toLowerCase();
        const email = membre.email.toLowerCase();
        const poste = (membre.poste || '').toLowerCase();
        if (!fullName.includes(search) && !email.includes(search) && !poste.includes(search)) return false;
      }
      
      // Filtre par contrat
      if (contratFilter !== 'tous' && membre.type_contrat !== contratFilter) return false;
      
      // Filtre par statut attestation
      if (statutFilter !== 'tous') {
        const status = getAttestationStatus(membre);
        if (statutFilter === 'conforme' && status !== 'conforme') return false;
        if (statutFilter === 'en_attente' && status !== 'en_attente' && status !== 'verification_en_cours') return false;
        if (statutFilter === 'a_regulariser' && status !== 'a_regulariser' && status !== 'expiree') return false;
      }
      
      return true;
    });
  }, [membres, contratFilter, statutFilter, searchTerm]);
  
  // Compteurs pour les onglets de filtre
  const counts = useMemo(() => {
    const c = { tous: membres.length, CDI: 0, CDD: 0, Benevole: 0, Stage: 0, Interim: 0 };
    for (const m of membres) {
      if (m.type_contrat in c) c[m.type_contrat as keyof typeof c]++;
    }
    return c;
  }, [membres]);

  const contratFilters: { value: ContratFilter; label: string }[] = [
    { value: 'tous', label: `Tous (${counts.tous})` },
    { value: 'CDI', label: `CDI (${counts.CDI})` },
    { value: 'CDD', label: `CDD (${counts.CDD})` },
    { value: 'Benevole', label: `Bénévoles (${counts.Benevole})` },
    { value: 'Stage', label: `Stagiaires (${counts.Stage})` },
    { value: 'Interim', label: `Intérim (${counts.Interim})` },
  ];
  
  const statutFilters: { value: StatutFilter; label: string; dot: string }[] = [
    { value: 'tous', label: 'Tous', dot: '' },
    { value: 'conforme', label: 'Conforme', dot: 'bg-green-500' },
    { value: 'en_attente', label: 'En cours', dot: 'bg-yellow-500' },
    { value: 'a_regulariser', label: 'À régulariser', dot: 'bg-red-500' },
  ];

  return (
    <div className="space-y-3.5">
      <div className="rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--app-text-muted))]" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Rechercher un membre par nom, email ou poste"
            className="h-10 w-full rounded-[14px] border border-[hsl(var(--app-border-subtle))] bg-white pl-10 pr-4 text-sm text-[hsl(var(--app-text))] outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
          />
        </div>

        <div className="mt-2.5 overflow-x-auto pb-1">
          <div className="flex min-w-max items-center gap-2">
            {contratFilters.map(f => (
              <button
                key={f.value}
                onClick={() => setContratFilter(f.value)}
                className={`rounded-[12px] px-3 py-1.5 text-[13px] font-medium transition-colors ${
                  contratFilter === f.value
                    ? 'bg-[hsl(var(--app-text-strong))] text-white'
                    : 'border border-[hsl(var(--app-border-subtle))] bg-white text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-muted))]'
                }`}
              >
                {f.value === 'tous' ? `Tous contrats (${counts.tous})` : f.label}
              </button>
            ))}

            <div className="mx-1 h-6 w-px shrink-0 bg-[hsl(var(--app-border-subtle))]" />

            {statutFilters.map(f => (
              <button
                key={f.value}
                onClick={() => setStatutFilter(f.value)}
                className={`flex items-center gap-1.5 rounded-[12px] px-3 py-1.5 text-[13px] font-medium transition-colors ${
                  statutFilter === f.value
                    ? 'bg-[hsl(var(--app-text-strong))] text-white'
                    : 'border border-[hsl(var(--app-border-subtle))] bg-white text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-muted))]'
                }`}
              >
                {f.dot && <span className={`h-2 w-2 rounded-full ${statutFilter === f.value ? 'bg-white' : f.dot}`} />}
                {f.value === 'tous' ? 'Tous statuts' : f.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Tableau */}
      <div className="overflow-hidden rounded-[20px] border border-[hsl(var(--app-border-subtle))] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] hover:bg-[hsl(var(--app-surface-muted))]">
              <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">
                Personne
              </TableHead>
              <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">
                Contrat
              </TableHead>
              <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">
                Attestation
              </TableHead>
              <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">
                Prise de réf.
              </TableHead>
              <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">
                Embauche
              </TableHead>
              <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">
                Renouvellement
              </TableHead>
              <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Skeleton
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell><div className="h-10 w-40 bg-gray-100 rounded animate-pulse" /></TableCell>
                  <TableCell><div className="h-6 w-16 bg-gray-100 rounded-full animate-pulse" /></TableCell>
                  <TableCell><div className="h-6 w-20 bg-gray-100 rounded-full animate-pulse" /></TableCell>
                  <TableCell><div className="h-6 w-16 bg-gray-100 rounded-full animate-pulse" /></TableCell>
                  <TableCell><div className="h-4 w-24 bg-gray-100 rounded animate-pulse" /></TableCell>
                  <TableCell><div className="h-4 w-24 bg-gray-100 rounded animate-pulse" /></TableCell>
                  <TableCell><div className="h-8 w-16 bg-gray-100 rounded animate-pulse" /></TableCell>
                </TableRow>
              ))
            ) : fetched && filteredMembres.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-14 text-center text-sm text-[hsl(var(--app-text-muted))]">
                  {membres.length === 0 
                    ? "Aucun membre dans l'équipe. Ajoutez votre premier membre."
                    : "Aucun membre ne correspond aux filtres sélectionnés."
                  }
                </TableCell>
              </TableRow>
            ) : (
              filteredMembres.map(membre => {
                const status = getAttestationStatus(membre);
                const initials = `${membre.prenom[0]}${membre.nom[0]}`.toUpperCase();
                
                return (
                  <TableRow 
                    key={membre.id} 
                    className="cursor-pointer border-b border-[hsl(var(--app-border-subtle))] transition-colors hover:bg-[hsl(var(--app-surface-muted))]/80"
                    onClick={() => onSelectMembre(membre)}
                  >
                    {/* Personne */}
                    <TableCell className="py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback 
                            className="text-white text-xs font-medium"
                            style={{ backgroundColor: '#40E0D0' }}
                          >
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="text-[13px] font-medium text-[hsl(var(--app-text-strong))]">
                            {membre.prenom} {membre.nom}
                          </span>
                          {membre.poste && (
                            <span className="text-[12px] text-[hsl(var(--app-text-muted))]">
                              {membre.poste}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    
                    {/* Contrat */}
                    <TableCell className="py-3">
                      <ContratBadge type={membre.type_contrat} />
                    </TableCell>
                    
                    {/* Attestation */}
                    <TableCell className="py-3">
                      <AttestationBadge status={status} />
                    </TableCell>
                    
                    {/* Prise de référence */}
                    <TableCell className="py-3">
                      <span className="text-[13px] text-[hsl(var(--app-text-muted))]">—</span>
                    </TableCell>
                    
                    {/* Date embauche */}
                    <TableCell className="py-3">
                      <span className="text-[13px] text-[hsl(var(--app-text))]">
                        {formatDateShort(membre.date_embauche)}
                      </span>
                    </TableCell>
                    
                    {/* Renouvellement */}
                    <TableCell className="py-3">
                      <RenouvellementCell date={membre.attestation?.date_renouvellement ?? null} />
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell className="py-3">
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        <button 
                          className="rounded-lg p-1.5 text-[hsl(var(--app-text-muted))] transition-colors hover:bg-[hsl(var(--app-surface-muted))] hover:text-[hsl(var(--app-text))]"
                          title="Voir le dossier"
                          onClick={() => onSelectMembre(membre)}
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        {status === 'conforme' && membre.attestation?.storage_path && (
                          <button 
                            className="rounded-lg p-1.5 text-[hsl(var(--app-text-muted))] transition-colors hover:bg-[hsl(var(--app-surface-muted))] hover:text-[hsl(var(--app-text))]"
                            title="Télécharger l'attestation"
                            onClick={async () => {
                              const storagePath = membre.attestation?.storage_path;
                              if (!storagePath) return;
                              const { data } = await supabase.storage
                                .from('attestations')
                                .createSignedUrl(storagePath, 60);
                              if (data?.signedUrl) {
                                window.open(data.signedUrl, '_blank');
                              }
                            }}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        {(status === 'en_attente' || status === 'a_regulariser' || status === 'expiree') && (
                          <button 
                            className="rounded-lg p-1.5 text-amber-600 transition-colors hover:bg-amber-50 hover:text-amber-700"
                            title="Relancer"
                            onClick={async () => {
                              const { error } = await supabase.functions.invoke('send-attestation-request', {
                                body: {
                                  membre_id: membre.id,
                                  email: membre.email,
                                  prenom: membre.prenom,
                                  nom: membre.nom,
                                  etablissement_id: membre.etablissement_id,
                                  type: 'rappel',
                                },
                              });
                              if (!error) {
                                toast({
                                  title: 'Rappel envoyé',
                                  description: `Un rappel a été envoyé à ${membre.prenom} ${membre.nom}.`,
                                });
                              }
                            }}
                          >
                            <Bell className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
